import { useState } from "react";
import { useAuth } from "@clerk/react";
import { useMutation } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Bug01Icon,
  Idea01Icon,
  CheckmarkCircle01Icon,
} from "@hugeicons/core-free-icons";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { createFeedbackApi, type FeedbackType } from "@/lib/api/feedback";
import { AnimatedShinyText } from "@/components/ui/animated-shiny-text";
import { DotPattern } from "@/components/ui/dot-pattern";
import { cn } from "@/lib/utils";
import confetti from "canvas-confetti";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const TYPES: { value: FeedbackType; label: string; icon: typeof Bug01Icon }[] =
  [
    { value: "bug", label: "Bug Report", icon: Bug01Icon },
    { value: "feature", label: "Feature Request", icon: Idea01Icon },
  ];

const PLACEHOLDERS: Record<FeedbackType, string> = {
  bug: "Describe what happened and how to reproduce it…",
  feature: "What would this feature do, and why would it help you?",
};

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const { getToken } = useAuth();
  const feedbackApi = createFeedbackApi({ getToken });

  const [type, setType] = useState<FeedbackType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: () => feedbackApi.submit({ type, title, description }),
    onSuccess: () => {
      setSubmitted(true);

      // Fire confetti
      const duration = 2000;
      const end = Date.now() + duration;

      const frame = () => {
        confetti({
          particleCount: 5,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ["#3b82f6", "#8b5cf6", "#10b981"],
        });
        confetti({
          particleCount: 5,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ["#3b82f6", "#8b5cf6", "#10b981"],
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };

      frame();

      setTimeout(() => {
        onOpenChange(false);
        // Reset after dialog closes
        setTimeout(() => {
          setSubmitted(false);
          setTitle("");
          setDescription("");
          setType("bug");
        }, 300);
      }, 2500);
    },
  });

  const canSubmit = title.trim().length > 0 && description.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] bg-card/60 backdrop-blur-3xl border-border/60 p-0 overflow-hidden shadow-2xl gap-0">
        <DotPattern
          width={20}
          height={20}
          cx={1}
          cy={1}
          cr={1}
          className={cn(
            "mask-[radial-gradient(400px_circle_at_center,white,transparent)]",
            "absolute inset-0 z-0 opacity-40 mix-blend-overlay",
          )}
        />

        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              transition={{ duration: 0.4, type: "spring", bounce: 0.3 }}
              className="relative z-10 flex flex-col items-center justify-center gap-5 py-20 px-8 text-center min-h-[460px]"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", bounce: 0.5 }}
                className="size-20 rounded-full bg-primary/10 flex items-center justify-center mb-2"
              >
                <HugeiconsIcon
                  icon={CheckmarkCircle01Icon}
                  className="size-10 text-primary"
                />
              </motion.div>
              <div className="space-y-2 relative z-20">
                <p className="text-2xl font-bold bg-linear-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
                  Thanks for the feedback!
                </p>
                <p className="text-sm text-balance text-muted-foreground max-w-[280px] mx-auto leading-relaxed">
                  Our team reviews every submission. You'll be notified if you
                  earn credits.
                </p>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative z-10 flex flex-col h-full"
            >
              {/* Header */}
              <div className="px-6 pt-7 pb-4">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-semibold tracking-tight text-foreground">
                    Send feedback
                  </DialogTitle>
                  <DialogDescription className="text-sm text-muted-foreground mt-1.5">
                    Help us improve Rayloop. Your voice matters.
                  </DialogDescription>
                </DialogHeader>
              </div>

              <div className="px-6 pb-6 flex flex-col gap-5">
                {/* Type toggle */}
                <div className="flex relative bg-muted/40 p-1.5 rounded-2xl border border-black/5 dark:border-white/5">
                  {TYPES.map(({ value, label, icon }) => {
                    const isActive = type === value;
                    return (
                      <button
                        key={value}
                        onClick={() => setType(value)}
                        className={cn(
                          "relative z-10 flex flex-1 items-center justify-center gap-2 rounded-xl p-2.5 text-sm font-semibold transition-colors duration-300",
                          isActive
                            ? "text-foreground"
                            : "text-muted-foreground hover:text-foreground/80",
                        )}
                      >
                        {isActive && (
                          <motion.div
                            layoutId="feedback-indicator"
                            className="absolute inset-0 bg-background rounded-xl shadow-sm border border-black/5 dark:border-white/5"
                            transition={{
                              type: "spring",
                              bounce: 0.2,
                              duration: 0.5,
                            }}
                          />
                        )}
                        <HugeiconsIcon
                          icon={icon}
                          className="size-4 shrink-0 relative z-20"
                        />
                        <span className="relative z-20">{label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Incentive callout */}
                <div className="group relative flex items-center justify-center text-center gap-3 rounded-xl bg-linear-to-b from-purple-500/5 to-purple-500/10 border border-purple-500/20 px-4 py-3.5 overflow-hidden transition-all hover:border-purple-500/30 hover:shadow-sm">
                  <div className="absolute inset-0 bg-linear-to-r from-purple-500/0 via-purple-500/5 to-purple-500/0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                  <p className="text-sm text-purple-900/80 dark:text-purple-200/80 leading-relaxed font-medium relative z-10">
                    Verified bugs and shipped feature requests earn you{" "}
                    <AnimatedShinyText className="inline-flex items-center justify-center font-bold px-0.5 cursor-default text-purple-900/80 dark:text-purple-200/80">
                      <span>free Rayloop credits</span>
                    </AnimatedShinyText>
                    .
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Title */}
                  <div className="flex flex-col gap-2 relative">
                    <label className="text-sm font-semibold text-foreground/90 ml-1">
                      Title
                    </label>
                    <Input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder={
                        type === "bug"
                          ? "Short summary of the bug"
                          : "Short summary of the feature"
                      }
                      className="bg-background/50 border-input hover:border-border text-foreground placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:border-primary transition-all shadow-sm rounded-xl h-11 px-4"
                    />
                  </div>

                  {/* Description */}
                  <div className="flex flex-col gap-2 relative">
                    <label className="text-sm font-semibold text-foreground/90 ml-1">
                      Description
                    </label>
                    <Textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={PLACEHOLDERS[type]}
                      rows={5}
                      className="bg-background/50 border-input hover:border-border text-foreground placeholder:text-muted-foreground resize-none focus-visible:ring-0 focus-visible:border-primary transition-all shadow-sm rounded-xl p-4"
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 pt-4 mt-2">
                  <Button
                    variant="ghost"
                    onClick={() => onOpenChange(false)}
                    disabled={mutation.isPending}
                    className="text-muted-foreground hover:text-foreground rounded-xl px-5"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={!canSubmit || mutation.isPending}
                    onClick={() => mutation.mutate()}
                    className="group relative bg-primary hover:bg-primary/90 text-primary-foreground min-w-[120px] rounded-xl shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5 overflow-hidden"
                  >
                    <span className="relative z-10 flex items-center gap-2 font-semibold">
                      {mutation.isPending ? "Submitting…" : "Submit"}
                    </span>
                    <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Button>
                </div>

                {mutation.isError && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-xs font-medium text-destructive text-center -mt-2 bg-destructive/10 py-1.5 rounded-lg border border-destructive/20"
                  >
                    Something went wrong. Please try again.
                  </motion.p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
